import { FlagsDisplay } from "@portal/components/layout/FlagsDisplay";
import { type FlagOptions, type FlagTone, type QueuedFlag } from "@portal/components/patterns/flag";
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

type GlobalFlagsProviderProps = {
	children: ReactNode;
};

export type GlobalFlagsDispatchContext = {
	showInfoFlag: (props: FlagOptions) => string;
	showWarningFlag: (props: FlagOptions) => string;
	showErrorFlag: (props: FlagOptions) => string;
	showSuccessFlag: (props: FlagOptions) => string;
	hideFlag: (id: string) => void;
};

const defaultGlobalFlagsDispatchContext: GlobalFlagsDispatchContext = {
	showInfoFlag: () => "",
	showWarningFlag: () => "",
	showErrorFlag: () => "",
	showSuccessFlag: () => "",
	hideFlag: () => {},
};

const FlagsDispatchContext = createContext<GlobalFlagsDispatchContext>(
	defaultGlobalFlagsDispatchContext,
);

const generateFlagHash = (
	tone: FlagTone,
	message: string,
	heading?: string,
	actions?: FlagOptions["actions"],
): string => {
	const actionsString = actions
		? actions.map((a) => `${a.label}:${a.actionId ?? a.label}`).join("-")
		: "";
	const string = `${tone}:${heading ?? ""}-${message}-${actionsString}`;
	let hash = 0;
	for (const char of string) {
		hash = (hash << 5) - hash + char.charCodeAt(0);
		hash |= 0; // Constrain to 32bit integer
	}
	return hash.toString();
};

export const GlobalFlagsProvider = ({ children }: GlobalFlagsProviderProps) => {
	const [shownFlags, setShownFlags] = useState<QueuedFlag[]>([]);

	const showFlag = useCallback((tone: FlagTone, options: FlagOptions) => {
		const flagId =
			options.id ?? generateFlagHash(tone, options.message, options.heading, options.actions);

		setShownFlags((prevFlags) =>
			prevFlags.some((flag) => flag.id === flagId)
				? prevFlags
				: [...prevFlags, { id: flagId, tone, ...options }],
		);

		return flagId;
	}, []);

	const hideFlag = useCallback((id: string) => {
		setShownFlags((prevFlags) => prevFlags.filter((flag) => flag.id !== id));
	}, []);

	const flagsDispatchContextValue = useMemo(
		() => ({
			showInfoFlag: (options: FlagOptions) => showFlag("info", options),
			showWarningFlag: (options: FlagOptions) => showFlag("warning", options),
			showErrorFlag: (options: FlagOptions) => showFlag("error", options),
			showSuccessFlag: (options: FlagOptions) => showFlag("success", options),
			hideFlag,
		}),
		[showFlag, hideFlag],
	);

	return (
		<FlagsDispatchContext.Provider value={flagsDispatchContextValue}>
			<FlagsDisplay flags={shownFlags} hideFlag={hideFlag}>
				{children}
			</FlagsDisplay>
		</FlagsDispatchContext.Provider>
	);
};

export const useFlags = () => {
	return useContext(FlagsDispatchContext);
};
